import React, { useEffect, useState } from "react";
import {
	  Flex,
	  Button,
	  Image,
	  Table,
	  Tbody,
	  Text,
	  Box,
	  Th,
  Td,
  Thead,
  Tr,
  IconButton,
  useColorModeValue,
  useToast,
  Tooltip,
  useBreakpointValue,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  PopoverCloseButton,
  PopoverBody,
} from "@chakra-ui/react";
import { EditIcon, DeleteIcon, CopyIcon } from "@chakra-ui/icons";
import Card from "components/Card/Card.js";
import CardHeader from "components/Card/CardHeader.js";
import CardBody from "components/Card/CardBody.js";
import ProductApi from "../../api/product";
import { useHistory } from "react-router-dom";
import { SearchBar } from "components/Input/SearchBar";
import Pagination from "components/Pagination/Pagination";

function Products() {
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [openTooltipIndex, setOpenTooltipIndex] = useState(null);
  const textColor = useColorModeValue("gray.700", "white");
  const history = useHistory();
  const toast = useToast();
  const isMobile = useBreakpointValue({ base: true, md: false });

  useEffect(() => {
    fetchProducts(currentPage);
  }, [search, currentPage]);

  const fetchProducts = async (page = 1) => {
    try {
      const response = await ProductApi.Products(search, page);
      setProducts(response.data.data);
      setTotalPages(response.data.meta.last_page);
    } catch (err) {
      console.error("Error fetching products:", err);
    }
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setCurrentPage(1);
  };

  const handleNavigateToAccounts = (productId) => {
    history.push(`/admin/products/${productId}/accounts`);
  };

  const handleEdit = (productId) => {
    history.push(`/admin/products/${productId}/edit`);
  };

  const handleDelete = (productId) => {
    console.log("Delete product with id:", productId);
  };

  const handleNavigation = () => {
    history.push(`/admin/products/free-z`);
  };

  const handleDailyProductListNavigation = () => {
    history.push(`/admin/products/daily-list`);
  };

  const getStockInfo = (attributes) => {
    const stockMap = {};
    attributes.forEach((attr) => {
      const key = `${attr.platform}-${attr.z_type}`;
      stockMap[key] = attr;
    });
    return stockMap;
  };

  const getUniqueCombinations = (products) => {
    const combinations = new Set();
    products.forEach((product) => {
      product.attributes.forEach((attr) => {
        combinations.add(`${attr.platform}-${attr.z_type}`);
      });
    });
    return Array.from(combinations);
  };

  const formatPrice = (price) => {
    return price.toLocaleString();
  };

  const generateCopyText = (attributes, productName) => {
    return [
      `🌟 قیمت بازی ${productName} ✅ \n`,
      ...attributes.map((attr) => {
        const formattedPrice = formatPrice(attr.price);
        const stockStatus = attr.stock === 0 ? " ❌" : "";
        return `🔥 Z${attr.z_type}${attr.platform.toUpperCase()} — 💰 ${formattedPrice}T${stockStatus}`;
      }),
    ].join("\n");
  };

  const copyToClipboard = (text) => {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text).then(() => {
        toast({
          title: "کپی شد",
          status: "success",
          duration: 2000,
          isClosable: true,
          position: "top-right",
        });
      });
    } else {
      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.style.position = "absolute";
      textArea.style.left = "-999999px";
      document.body.prepend(textArea);
      textArea.select();
      try {
        document.execCommand("copy");
        toast({
          title: "کپی شد",
          status: "success",
          duration: 2000,
          isClosable: true,
          position: "top-right",
        });
      } catch (error) {
        console.error(error);
      } finally {
        textArea.remove();
      }
    }
  };

  const uniqueCombinations = getUniqueCombinations(products);

  return (
    <Flex direction="column" pt={{ base: "120px", md: "75px" }}>
      <Card overflowX={{ sm: "scroll", xl: "hidden" }}>
        <CardHeader
          p="6px 0px 22px 0px"
          display="flex"
          alignItems="center"
          justifyContent="space-between"
        >
          <Box>
            <Text fontSize="xl" color={textColor} fontWeight="bold" display="inline">
              جدول محصولات
            </Text>
            <Button mr="3" colorScheme="teal" size="sm" onClick={handleNavigation}>
              لیست موجودی
            </Button>
            <Button
              mr="3"
              colorScheme="green"
              size="sm"
              onClick={handleDailyProductListNavigation}
            >
              لیست روزانه
            </Button>
          </Box>
          <SearchBar value={search} onChange={handleSearchChange} />
        </CardHeader>
        <CardBody>
          <Table variant="simple" color={textColor}>
            <Thead>
              <Tr my=".8rem" pl="0px" color="gray.400">
                <Th pl="0px" color="gray.400" width="20%">
                  نام
                </Th>
                {uniqueCombinations.map((combination, index) => (
                  <Th key={index} color="gray.400">
                    {combination}
                  </Th>
                ))}
                <Th></Th>
              </Tr>
            </Thead>
	            <Tbody>
	              {products.map((product) => {
	                const stockInfo = getStockInfo(product.attributes);
	                const copyText = generateCopyText(product.attributes, product.name);
	                return (
	                  <Tr key={product.id}>
	                    <Td>
	                      <Flex align="center" gap="10px">
	                        {product.poster?.thumb_url || product.poster?.url ? (
	                          <Image
	                            src={product.poster?.thumb_url || product.poster?.url}
	                            alt={product.name}
	                            boxSize="36px"
	                            objectFit="cover"
	                            borderRadius="md"
	                          />
	                        ) : (
	                          <Box
	                            boxSize="36px"
	                            borderRadius="md"
	                            bg="gray.100"
	                            borderWidth="1px"
	                            borderColor="gray.200"
	                          />
	                        )}
	                        <Text>{product.name}</Text>
	                      </Flex>
	                    </Td>
	                    {uniqueCombinations.map((combination, index) => {
	                      const attr = stockInfo[combination];
	                      const tooltipKey = `${product.id}-${index}`;
	
	                      if (!attr)
                        return (
                          <Td key={index} textAlign="center" color="gray.400">
                            -
                          </Td>
                        );

                      const content = (
                        <>
                          <Text>قیمت: {formatPrice(attr.price)} تومان</Text>
                          <Text color={attr.stock === 0 ? "red.400" : "green.500"}>
                            {attr.stock === 0 ? "ناموجود" : "موجود"}
                          </Text>
                        </>
                      );

                      return isMobile ? (
                        <Td key={index} textAlign="center">
                          <Popover
                            isOpen={openTooltipIndex === tooltipKey}
                            onClose={() => setOpenTooltipIndex(null)}
                            placement="top"
                          >
                            <PopoverTrigger>
                              <Box
                                onClick={() =>
                                  setOpenTooltipIndex(
                                    openTooltipIndex === tooltipKey ? null : tooltipKey
                                  )
                                }
                                cursor="pointer"
                                userSelect="none"
                                fontWeight="bold"
                              >
                                {attr.stock}
                              </Box>
                            </PopoverTrigger>
                            <PopoverContent w="fit-content">
                              <PopoverArrow />
                              <PopoverCloseButton />
                              <PopoverBody>{content}</PopoverBody>
                            </PopoverContent>
                          </Popover>
                        </Td>
                      ) : (
                        <Tooltip
                          key={index}
                          label={content}
                          hasArrow
                          placement="top"
                          isInteractive
                        >
                          <Td textAlign="center" cursor="default">
                            <Box fontWeight="bold">{attr.stock}</Box>
                          </Td>
                        </Tooltip>
                      );
                    })}
                    <Td textAlign="center">
                      <Button
                        size="sm"
                        colorScheme="teal"
                        bg="teal.500"
                        color="white"
                        mr={2}
                        onClick={() => handleNavigateToAccounts(product.id)}
                      >
                        اکانت ها
                      </Button>
                      <IconButton
                        size="sm"
                        colorScheme="blue"
                        aria-label="Edit"
                        icon={<EditIcon />}
                        onClick={() => handleEdit(product.id)}
                        mr={1}
                      />
                      <IconButton
                        size="sm"
                        colorScheme="red"
                        aria-label="Delete"
                        icon={<DeleteIcon />}
                        onClick={() => handleDelete(product.id)}
                        mr={1}
                      />
                      <IconButton
                        size="sm"
                        colorScheme="gray"
                        aria-label="Copy"
                        icon={<CopyIcon />}
                        onClick={() => copyToClipboard(copyText)}
                      />
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </CardBody>
      </Card>
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
      />
    </Flex>
  );
}

export default Products;
